import { ImagePaths } from '../../lib/imageUrls.js';

export const ai300MlOpsEngineerAssociate = {
    id: 21,
    slug: 'ai-300-ml-ops-engineer-associate',
    ogImage: '/og/courses/ai-300-ml-ops-engineer-associate.jpg',
    courseCode: 'AI-300-001',
    featured: false,
    title: 'AI-300: MLOps Engineer Associate',
    subtitle: 'Operationalise machine learning & GenAIOps on Azure',

    valueProposition: 'Take models from prototype to production with secure, scalable MLOps practices and automation tooling. Learn how to build end-to-end GenAIOps pipelines using Azure Machine Learning, Microsoft Foundry and GitHub Actions while ensuring quality, observability and compliance with Australian frameworks.',

    duration: '24 hours',
    students: 'New',
    level: 'Foundational',
    organization: 'Microsoft',
    image: ImagePaths.COURSE_1,
    shortDescription: 'AI-300-001 — Azure MLOps and GenAIOps training covering model deployment, monitoring, and pipeline automation for ML engineers and ops teams.',

    price: {
      aud: 299,
      currency: 'AUD',
      earlyBird: 249
    },

    stripeConfig: {
    priceId: 'price_1T9bEAKu1nyc322UAKo2XQCx',
    productId: 'prod_U7qwMNRpQkIJLr'
  },

    snapshot: {
      whoItsFor: [
        'ML engineers and data scientists operationalising models',
        'DevOps/Platform engineers building AI infrastructure',
        'AI Ops teams responsible for monitoring and governance',
        'Cloud architects designing secure AI platforms',
        'Developers preparing for the Microsoft AI-300 certification',
        'Organisations automating generative AI workflows'
      ],
      accreditation: 'Preparation for Microsoft AI-300: MLOps Engineer Associate certification'
    },

    logistics: {
      classSize: 'Maximum 16 participants for intensive hands-on labs',
      requirements: [
        'Computer with modern web browser and development environment',
        'Active Microsoft Azure subscription (free tier acceptable)',
        'Visual Studio Code or preferred IDE installed',
        'Reliable internet connection (minimum 15 Mbps)',
        'Basic experience with Python or PowerShell (or equivalent)',
        'Familiarity with Git, Azure CLI and YAML pipelines helpful',
        'Understanding of machine learning concepts beneficial'
      ]
    },

    overview: 'This 24-hour intermediate course teaches you how to take ML and generative AI workloads from research to production on Azure. You\'ll build secure, scalable AI environments using Bicep and GitHub Actions, manage model lifecycles in Azure Machine Learning, and automate GenAIOps pipelines with Microsoft Foundry. Quality assurance, observability and performance optimisation are emphasised alongside responsible AI practices tied to Australian Privacy Principles, Essential Eight and ISO 27001. Ideal for engineers, ops teams and organisations looking to embed MLOps principles within their AI delivery processes.',

    outcomes: [
      'Provision secure, scalable AI infrastructure with Bicep, Azure CLI and GitHub Actions',
      'Register, version and deploy models using Azure Machine Learning with progressive rollouts',
      'Automate generative AI pipelines end-to-end using Microsoft Foundry and GitHub workflows',
      'Implement tracing, safety evaluations and drift monitoring using Promptflow and Azure Monitor',
      'Optimise performance of RAG pipelines, embedding models and synthetic training data',
      'Apply responsible AI and compliance controls aligned to APPs, Essential Eight and ISO 27001',
      'Set up alerting, logging and incident response for production AI systems',
      'Prepare for AI-300 certification through hands-on exam-style scenarios'
    ],

    audience: [
      'ML engineers, data scientists and AI Ops practitioners',
      'DevOps and platform engineers supporting AI workloads',
      'Cloud architects and solution designers',
      'Developers building generative AI applications',
      'IT professionals responsible for compliance and security',
      'Teams preparing for AI-300 qualification'
    ],

    prerequisites: [
      'Basic understanding of Azure fundamentals or completion of AI-901',
      'Experience writing scripts in Python, PowerShell or similar',
      'Familiarity with Git, CLI tooling and YAML pipelines',
      'General knowledge of machine learning lifecycle concepts',
      'Access to an Azure subscription (free tier is acceptable)'
    ],

    modules: [
      {
        title: 'AI Operations (AIOps) Infrastructure',
        duration: '5 hours',
        topics: [
          'Designing secure AI network topologies and private endpoints',
          'Provisioning resources with Bicep and Azure CLI',
          'Automating deployments via GitHub Actions',
          'Configuring managed identities and key vault integration',
          'Cost management and resource tagging practices',
          'Aligning infrastructure to Essential Eight and ISO 27001'
        ]
      },
      {
        title: 'ML Model Lifecycle Management',
        duration: '6 hours',
        topics: [
          'Registering and versioning models in Azure Machine Learning',
          'Creating pipelines for training and validation',
          'Progressive deployment strategies and blue/green rollouts',
          'Tracking lineage and metadata for auditability',
          'Managing datasets and feature stores',
          'Governance controls and access policies'
        ]
      },
      {
        title: 'GenAIOps Infrastructure',
        duration: '5 hours',
        topics: [
          'Introduction to Microsoft Foundry for generative workloads',
          'Building end-to-end automation pipelines',
          'Integrating LLMs with external data sources and APIs',
          'Versioning prompts and evaluation artefacts',
          'Scheduling and error handling in workflows',
          'Scaling GenAI services for production use'
        ]
      },
      {
        title: 'Quality Assurance & Observability',
        duration: '4 hours',
        topics: [
          'Implementing Promptflow tracing and test suites',
          'Setting up safety evaluation jobs and scoring',
          'Monitoring data and model drift with Azure Monitor',
          'Configuring alerts and dashboards',
          'Logging strategies for generative AI outputs',
          'Compliance reporting for APPs and audit requirements'
        ]
      },
      {
        title: 'Performance Optimisation',
        duration: '4 hours',
        topics: [
          'Tuning retrieval-augmented generation (RAG) pipelines',
          'Fine-tuning and adapting embedding models',
          'Using synthetic data to improve training velocity',
          'Resource scaling and GPU utilisation best practice',
          'Cost-effective model hosting and inference strategies'
        ]
      }
    ],

    format: {
      type: 'Live online with hands-on labs',
      includes: [
        '24 hours of instructor-led training over three days',
        'Practical labs using real Azure services and Microsoft Foundry',
        'Course workbook, templates and code samples',
        'Exam-style scenarios for AI-300 certification preparation',
        'Responsible AI & compliance checklists',
        'Certificate of completion',
        '6 months access to materials',
        'Invitation to private Q&A community'
      ]
    },

    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'Three-day intensive format or six half-days for working professionals'
    },

    // Pathway relationships
    pathways: [
      'ai-foundations-professional',
      'azure-ai-engineer-certification'
    ],

    skillsTaught: [
      'MLOps pipelines',
      'Azure Machine Learning',
      'GenAIOps with Microsoft Foundry',
      'Model monitoring and drift detection',
      'CI/CD for ML',
      'Responsible AI compliance'
    ],

    hasCapstone: true,

    nextCourses: [
      'ai-master-prompt-engineering'
    ],

    faqs: [
      {
        question: 'Who should attend this course?',
        answer: 'This course is aimed at engineers and ops teams responsible for deploying and managing machine learning or generative AI systems in Azure. If you work on model deployment, monitoring or automation, this training will give you the practical skills you need.'
      },
      {
        question: 'Do I need to be a developer?',
        answer: 'Basic scripting ability (Python, PowerShell or similar) is required, but you don\'t have to be a software developer. Familiarity with command-line tools and YAML pipelines is more important than writing production code.'
      },
      {
        question: 'Will this help me prepare for the AI-300 certification?',
        answer: 'Yes – the curriculum maps directly to the AI-300 exam objectives and includes hands-on exercises and exam-style scenarios to boost your confidence. You\'ll also receive study resources and practice questions.'
      },
      {
        question: 'How are Australian compliance frameworks covered?',
        answer: 'Throughout the course we reference the Australian Privacy Principles (APPs), Essential Eight security controls and ISO 27001. Labs include tasks such as implementing network segmentation, audit logging and access policies that align with these frameworks.'
      },
      {
        question: 'Will I incur Azure charges during labs?',
        answer: 'Labs are designed to stay within the Azure free tier where possible. We provide guidance on creating cost-monitored subscriptions and cleaning up resources; typical costs are less than $20 AUD per attendee.'
      },
      {
        question: 'Can the course be customised for our organisation?',
        answer: 'Absolutely – we can tailor examples and labs to your use cases, data sources and tools. In-house delivery allows us to align content with your governance and infrastructure standards.'
      },
      {
        question: 'What support do I get after the course?',
        answer: 'You\'ll have 6 months access to all materials.'
      }
    ]
};